import React, { useState, useEffect } from 'react';
import { Search, Check, Info, GraduationCap, X, ChevronDown, ChevronUp } from 'lucide-react';

const SelectorMaterias = ({ materias, materiasSeleccionadas, setMateriasSeleccionadas }) => {
  const [busqueda, setBusqueda] = useState('');
  const [materiasFiltradas, setMateriasFiltradas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [materiaModal, setMateriaModal] = useState(null);
  const [categoriasExpandidas, setCategoriasExpandidas] = useState({});
  
  // Agrupar materias por categoría y filtrar cuando la búsqueda cambie
  useEffect(() => {
    if (!materias || materias.length === 0) return;
    
    // Obtener categorías únicas
    const categoriasUnicas = [...new Set(materias.map(m => m.categoria))];
    setCategorias(categoriasUnicas);
    
    // Inicializar todas las categorías como contraídas (no expandidas)
    if (Object.keys(categoriasExpandidas).length === 0) {
      const estadoInicial = {};
      categoriasUnicas.forEach(cat => {
        estadoInicial[cat] = false; // Inicialmente contraídas
      });
      setCategoriasExpandidas(estadoInicial);
    }
    
    // Filtrar materias según la búsqueda
    if (busqueda.trim() === '') {
      setMateriasFiltradas(materias);
      
      // Si se borra la búsqueda, contraer todas las categorías
      if (Object.keys(categoriasExpandidas).length > 0) {
        const categoriasCerradas = {};
        categoriasUnicas.forEach(cat => {
          // Mantener expandidas solo las que el usuario haya expandido manualmente
          const tieneMateriasSeleccionadas = materiasSeleccionadas.some(m => m.categoria === cat);
          categoriasCerradas[cat] = tieneMateriasSeleccionadas;
        });
        setCategoriasExpandidas(categoriasCerradas);
      }
    } else {
      const termino = busqueda.toLowerCase().trim();
      const filtradas = materias.filter(m => 
        m.nombremateria.toLowerCase().includes(termino) || 
        m.categoria.toLowerCase().includes(termino) ||
        (m.descripcionMateria && m.descripcionMateria.toLowerCase().includes(termino))
      );
      setMateriasFiltradas(filtradas);
      
      // Si hay búsqueda, expandir automáticamente todas las categorías que tengan resultados
      if (filtradas.length > 0) {
        const categoriasFiltradas = [...new Set(filtradas.map(m => m.categoria))];
        const nuevoEstado = { ...categoriasExpandidas };
        categoriasFiltradas.forEach(cat => {
          nuevoEstado[cat] = true;
        });
        setCategoriasExpandidas(nuevoEstado);
      }
    }
  }, [materias, busqueda, materiasSeleccionadas]);
  
  // Manejar selección de materias
  const toggleSeleccionMateria = (materia) => {
    const nuevasSeleccionadas = [...materiasSeleccionadas];
    const indice = nuevasSeleccionadas.findIndex(m => m.nombremateria === materia.nombremateria);
    
    if (indice >= 0) {
      nuevasSeleccionadas.splice(indice, 1);
    } else {
      nuevasSeleccionadas.push(materia);
    }
    
    setMateriasSeleccionadas(nuevasSeleccionadas);
  };
  
  // Verificar si una materia está seleccionada
  const estaSeleccionada = (materia) => {
    return materiasSeleccionadas.some(m => m.nombremateria === materia.nombremateria);
  };
  
  // Alterna expandir/contraer una categoría
  const toggleCategoria = (categoria) => {
    setCategoriasExpandidas(prev => ({
      ...prev,
      [categoria]: !prev[categoria]
    }));
  };
  
  // Manejar cambio en el buscador
  const handleBusquedaChange = (e) => {
    const valor = e.target.value;
    setBusqueda(valor);
  };
  
  // Limpiar la búsqueda y contraer categorías
  const limpiarBusqueda = () => {
    setBusqueda('');
    
    // Si se limpia la búsqueda, contraer todas las categorías
    if (categorias.length > 0) {
      const categoriasCerradas = {};
      categorias.forEach(cat => {
        // Mantener expandidas solo las que tienen materias seleccionadas
        const tieneMateriasSeleccionadas = materiasSeleccionadas.some(m => m.categoria === cat);
        categoriasCerradas[cat] = tieneMateriasSeleccionadas;
      });
      setCategoriasExpandidas(categoriasCerradas);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center text-xl font-bold text-gray-800 mb-4">
        <GraduationCap className="mr-3 w-6 h-6 text-blue-500" />
        Materias
      </div>
      
     <p className="text-gray-600 mb-4">
      Selecciona las materias en las que te especializas y que <span className="font-bold">manejes muy bien</span>. Recuerda que solo recibirás solicitudes de las materias que selecciones.
      <span className="block mt-2 text-blue-600 bg-blue-50 p-2 rounded border-l-4 border-blue-500">
        <strong>Importante:</strong> Si ofreces servicios de realización de trabajos escritos, asegúrate de seleccionar la materia "TRABAJOS" en la categoría "TRABAJOS".
      </span>
    </p>
      
      {/* Resumen de materias seleccionadas */}
      {materiasSeleccionadas.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg mb-4">
          <div className="font-medium text-blue-700 mb-2">
            Materias seleccionadas ({materiasSeleccionadas.length}):
          </div>
          <div className="flex flex-wrap gap-2">
            {materiasSeleccionadas.map((materia, idx) => (
              <div 
                key={idx} 
                className="flex items-center bg-white px-3 py-1 rounded-full border border-blue-200"
              >
                <span className="text-sm">{materia.nombremateria}</span>
                <button 
                  className="ml-2 text-gray-400 hover:text-red-500"
                  onClick={() => toggleSeleccionMateria(materia)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Buscador de materias */}
      <div className="relative mb-6">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Search className="w-5 h-5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="Buscar materia..."
          value={busqueda}
          onChange={handleBusquedaChange}
        />
        {busqueda && (
          <button 
            className="absolute inset-y-0 right-0 flex items-center pr-3"
            onClick={limpiarBusqueda}
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        )}
      </div>
      
      {/* Lista de materias por categoría */}
      <div className="space-y-6">
        {categorias.map((categoria) => {
          // Filtrar materias por categoría
          const materiasCategoria = materiasFiltradas.filter(m => m.categoria === categoria);
          
          // Si no hay materias en esta categoría después del filtrado, no mostrar la categoría
          if (materiasCategoria.length === 0) return null;
          
          return (
            <div key={categoria} className="border rounded-lg overflow-hidden">
              {/* Encabezado de categoría clickeable */}
              <div 
                className="bg-gray-50 p-4 font-medium border-b flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleCategoria(categoria)}
              >
                <span>{categoria}</span>
                {categoriasExpandidas[categoria] ? 
                  <ChevronUp className="w-5 h-5 text-gray-500" /> : 
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                }
              </div>
              
              {/* Contenido de la categoría (materias) */}
              {categoriasExpandidas[categoria] && (
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {materiasCategoria.map((materia, index) => (
                    <div 
                      key={index}
                      className={`flex items-center p-3 border rounded-md cursor-pointer hover:bg-blue-50 transition-colors ${
                        estaSeleccionada(materia) ? 'bg-blue-50 border-blue-200' : 'hover:border-blue-200'
                      }`}
                    >
                      <div 
                        className="flex-grow flex items-center"
                        onClick={() => toggleSeleccionMateria(materia)}
                      >
                        <div className={`w-5 h-5 rounded-md mr-2 flex items-center justify-center ${
                          estaSeleccionada(materia) 
                            ? 'bg-blue-500 text-white' 
                            : 'border border-gray-300'
                        }`}>
                          {estaSeleccionada(materia) && <Check className="w-3 h-3" />}
                        </div>
                        <span>{materia.nombremateria}</span>
                      </div>
                      
                      {/* Botón de información */}
                      <button 
                        className="ml-2 text-gray-400 hover:text-blue-500 transition-colors"
                        onClick={() => setMateriaModal(materia)}
                      >
                        <Info className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Modal de detalles de materia */}
      {materiaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">{materiaModal.nombremateria}</h3>
              <button 
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setMateriaModal(null)}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mb-3">
                {materiaModal.categoria}
              </span>
              
              <p className="text-gray-700 mb-4">{materiaModal.descripcionMateria || "No hay descripción disponible."}</p>
              
              {materiaModal.precioAsesoria > 0 && (
                <div className="mb-2">
                  <p className="text-sm font-medium">Precio de asesoría: ${materiaModal.precioAsesoria.toLocaleString()}</p>
                  {materiaModal.comentarioPrecioAsesoria && (
                    <p className="text-xs text-gray-500">{materiaModal.comentarioPrecioAsesoria}</p>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button 
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => setMateriaModal(null)}
              >
                Cerrar
              </button>
              <button 
                className={`px-4 py-2 rounded-md ${
                  estaSeleccionada(materiaModal) 
                    ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
                onClick={() => {
                  toggleSeleccionMateria(materiaModal);
                  setMateriaModal(null);
                }}
              >
                {estaSeleccionada(materiaModal) ? 'Deseleccionar' : 'Seleccionar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectorMaterias;